import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAuditLog, useContentVersion } from "@/hooks/useContentOperations";
import { seoSchema, validateNoindex, type SEOData } from "@/lib/contentValidation";
import AdminHeader from "@/components/admin/AdminHeader";
import ImageUploader, { type ImageData } from "@/components/admin/ImageUploader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Save,
  Send,
  Archive,
  Eye,
  FileText,
  Search,
  Plus,
  CheckCircle,
  Clock,
  AlertTriangle,
  History,
  RotateCcw,
  Image as ImageIcon,
} from "lucide-react";

interface ContentItemRow {
  id: string;
  type: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: Record<string, any>;
  seo: Record<string, any>;
  status: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

interface VersionRow {
  id: string;
  snapshot: Record<string, any>;
  created_at: string;
  created_by: string | null;
}

const statusColors: Record<string, string> = {
  published: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  draft: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  review: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  archived: "bg-muted text-muted-foreground",
};

const statusIcons: Record<string, React.ElementType> = {
  published: CheckCircle,
  draft: Clock,
  review: Eye,
  archived: Archive,
};

const typeLabels: Record<string, string> = {
  blog: "Blog Post",
  guide: "Guide",
  case_study: "Case Study",
  faq: "FAQ",
};

const ContentItemsManager = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const logAudit = useAuditLog();
  const saveVersion = useContentVersion();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [type, setType] = useState("blog");
  const [excerpt, setExcerpt] = useState("");
  const [contentJson, setContentJson] = useState("{}");
  const [seoData, setSeoData] = useState<SEOData>({
    title: "",
    description: "",
    canonicalUrl: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    indexable: true,
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [thumbnailImage, setThumbnailImage] = useState<ImageData | null>(null);
  const [heroImage, setHeroImage] = useState<ImageData | null>(null);
  const [ogImage, setOgImage] = useState<ImageData | null>(null);

  // Fetch all content items
  const { data: items, isLoading } = useQuery({
    queryKey: ["admin-content-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_items")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data as unknown as ContentItemRow[];
    },
  });

  // Fetch versions
  const { data: versions } = useQuery({
    queryKey: ["content-item-versions", selectedId],
    queryFn: async () => {
      if (!selectedId) return [];
      const { data, error } = await supabase
        .from("content_versions")
        .select("*")
        .eq("entity_type", "content_item")
        .eq("entity_id", selectedId)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data as VersionRow[];
    },
    enabled: !!selectedId,
  });

  const selectedItem = items?.find((i) => i.id === selectedId);

  // Load item data
  useEffect(() => {
    if (selectedItem) {
      setTitle(selectedItem.title);
      setSlug(selectedItem.slug);
      setType(selectedItem.type);
      setExcerpt(selectedItem.excerpt || "");
      setContentJson(JSON.stringify(selectedItem.content, null, 2));
      const seo = selectedItem.seo || {};
      setSeoData({
        title: seo.title || "",
        description: seo.description || "",
        canonicalUrl: seo.canonicalUrl || "",
        ogTitle: seo.ogTitle || "",
        ogDescription: seo.ogDescription || "",
        ogImage: seo.ogImage || "",
        indexable: seo.indexable !== false,
      });
      // Load images from content
      const c = selectedItem.content || {};
      setThumbnailImage(c.thumbnail_image || null);
      setHeroImage(c.hero_image || null);
      setOgImage(c.og_image || null);
      setValidationErrors([]);
      setIsCreating(false);
    }
  }, [selectedId, selectedItem?.updated_at]);

  // Filter
  const filteredItems = items?.filter((i) => {
    const matchesType = typeFilter === "all" || i.type === typeFilter;
    const matchesStatus = statusFilter === "all" || i.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      i.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.slug.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  // Start new item
  const handleNew = () => {
    setSelectedId(null);
    setIsCreating(true);
    setTitle("");
    setSlug("");
    setType("blog");
    setExcerpt("");
    setContentJson('{"category": "", "readTime": "", "author": "XperfoMax Team", "blocks": []}');
    setSeoData({
      title: "",
      description: "",
      canonicalUrl: "",
      ogTitle: "",
      ogDescription: "",
      ogImage: "",
      indexable: true,
    });
    setThumbnailImage(null);
    setHeroImage(null);
    setOgImage(null);
    setValidationErrors([]);
  };

  // Auto-generate slug from title
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    if (isCreating || !selectedItem) {
      const autoSlug = newTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      setSlug(autoSlug);
    }
  };

  // Parse content JSON safely
  const parseContent = (): Record<string, any> | null => {
    try {
      return JSON.parse(contentJson);
    } catch {
      return null;
    }
  };

  /** Merge images into content JSONB before persisting */
  const buildContentWithImages = (content: Record<string, any>): Record<string, any> => {
    const merged = { ...content };
    merged.thumbnail_image = thumbnailImage || undefined;
    merged.hero_image = heroImage || undefined;
    merged.og_image = ogImage || undefined;
    // Clean undefined keys
    if (!merged.thumbnail_image) delete merged.thumbnail_image;
    if (!merged.hero_image) delete merged.hero_image;
    if (!merged.og_image) delete merged.og_image;
    return merged;
  };

  // Save (create or update)
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      if (!title.trim()) throw new Error("Title is required");
      if (!slug.trim()) throw new Error("Slug is required");
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
        throw new Error("Slug must be lowercase alphanumeric with hyphens only");
      }

      const rawContent = parseContent();
      if (!rawContent) throw new Error("Content JSON is invalid");
      const content = buildContentWithImages(rawContent);

      setValidationErrors([]);

      if (isCreating) {
        const { data, error } = await supabase
          .from("content_items")
          .insert({
            type,
            title: title.trim(),
            slug: slug.trim(),
            excerpt: excerpt.trim() || null,
            content,
            seo: seoData,
            status: "draft",
            updated_by: user.id,
          })
          .select()
          .single();
        if (error) {
          if (error.code === "23505") throw new Error("Slug already exists");
          throw error;
        }
        setSelectedId(data.id);
        setIsCreating(false);
        await logAudit({
          action: "create",
          entityType: "content_item",
          entityId: data.id,
        });
      } else if (selectedItem) {
        const { error } = await supabase
          .from("content_items")
          .update({
            title: title.trim(),
            slug: slug.trim(),
            excerpt: excerpt.trim() || null,
            content,
            seo: { ...seoData, ogImage: ogImage?.src || seoData.ogImage },
            updated_by: user.id,
          })
          .eq("id", selectedItem.id);
        if (error) throw error;
        await logAudit({
          action: "save",
          entityType: "content_item",
          entityId: selectedItem.id,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-content-items"] });
      queryClient.invalidateQueries({ queryKey: ["content-items"] });
      toast.success(isCreating ? "Item created" : "Changes saved");
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Publish
  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!selectedItem || !user) throw new Error("No item selected");

      const seoResult = seoSchema.safeParse(seoData);
      if (!seoResult.success) {
        const errors = seoResult.error.errors.map((e) => e.message);
        setValidationErrors(errors);
        throw new Error("Fix SEO validation errors before publishing");
      }

      const rawContent = parseContent();
      if (!rawContent) throw new Error("Content JSON is invalid");
      const content = buildContentWithImages(rawContent);

      // Validate images have alt text
      if (thumbnailImage && !thumbnailImage.alt.trim()) {
        setValidationErrors(prev => [...prev, "Thumbnail image requires alt text"]);
        throw new Error("Fix validation errors before publishing");
      }

      // Save version
      await saveVersion({
        entityType: "content_item",
        entityId: selectedItem.id,
        snapshot: { title, slug, excerpt, content, seo: seoData },
      });

      // Save + publish
      const { error } = await supabase
        .from("content_items")
        .update({
          title: title.trim(),
          slug: slug.trim(),
          excerpt: excerpt.trim() || null,
          content,
          seo: { ...seoData, ogImage: ogImage?.src || seoData.ogImage },
          status: "published",
          published_at: new Date().toISOString(),
          updated_by: user.id,
        })
        .eq("id", selectedItem.id);
      if (error) throw error;

      await logAudit({
        action: "publish",
        entityType: "content_item",
        entityId: selectedItem.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-content-items"] });
      queryClient.invalidateQueries({ queryKey: ["content-items"] });
      toast.success("Published");
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Archive
  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedItem || !user) return;
      const { error } = await supabase
        .from("content_items")
        .update({ status: "archived", updated_by: user.id })
        .eq("id", selectedItem.id);
      if (error) throw error;
      await logAudit({
        action: "archive",
        entityType: "content_item",
        entityId: selectedItem.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-content-items"] });
      toast.success("Archived");
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Status change
  const changeStatus = useMutation({
    mutationFn: async (newStatus: string) => {
      if (!selectedItem || !user) return;
      const { error } = await supabase
        .from("content_items")
        .update({ status: newStatus, updated_by: user.id })
        .eq("id", selectedItem.id);
      if (error) throw error;
      await logAudit({
        action: `status_change_${newStatus}`,
        entityType: "content_item",
        entityId: selectedItem.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-content-items"] });
      toast.success("Status updated");
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Rollback
  const rollbackMutation = useMutation({
    mutationFn: async (versionId: string) => {
      if (!selectedItem || !user) return;
      const { data: version, error: fetchErr } = await supabase
        .from("content_versions")
        .select("snapshot")
        .eq("id", versionId)
        .single();
      if (fetchErr || !version) throw new Error("Version not found");

      const snap = version.snapshot as Record<string, any>;
      const { error } = await supabase
        .from("content_items")
        .update({
          title: snap.title,
          slug: snap.slug,
          excerpt: snap.excerpt,
          content: snap.content,
          seo: snap.seo || {},
          updated_by: user.id,
        })
        .eq("id", selectedItem.id);
      if (error) throw error;

      await logAudit({
        action: "rollback",
        entityType: "content_item",
        entityId: selectedItem.id,
        metadata: { restored_version_id: versionId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-content-items"] });
      queryClient.invalidateQueries({ queryKey: ["content-items"] });
      toast.success("Restored from previous version");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const formatDate = (d: string) =>
    new Date(d).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const previewUrl = selectedItem
    ? `/${selectedItem.type === "case_study" ? "case-studies" : "blog"}/${selectedItem.slug}?preview=true`
    : null;

  const showEditor = isCreating || selectedItem;

  return (
    <div className="min-h-screen">
      <AdminHeader
        title="Content Items"
        description="Manage blog posts, guides, and case studies"
      />

      <main className="p-6">
        <div className="flex gap-6">
          {/* Left: Item List */}
          <div className="w-72 flex-shrink-0 space-y-3">
            <Button onClick={handleNew} className="w-full" size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Item
            </Button>

            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="blog">Blog</SelectItem>
                    <SelectItem value="guide">Guide</SelectItem>
                    <SelectItem value="case_study">Case Study</SelectItem>
                    <SelectItem value="faq">FAQ</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-card border rounded-lg divide-y divide-border max-h-[calc(100vh-280px)] overflow-y-auto">
              {isLoading ? (
                <div className="p-3 space-y-2">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : filteredItems?.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4 text-center">No items found</p>
              ) : (
                filteredItems?.map((item) => {
                  const StatusIcon = statusIcons[item.status] || FileText;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { setIsCreating(false); setSelectedId(item.id); }}
                      className={`w-full text-left px-3 py-3 text-sm transition-colors ${
                        selectedId === item.id && !isCreating ? "bg-primary/10" : "hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`h-3.5 w-3.5 flex-shrink-0 ${
                          item.status === "published" ? "text-green-500" :
                          item.status === "draft" ? "text-yellow-500" :
                          item.status === "review" ? "text-blue-500" : "text-muted-foreground"
                        }`} />
                        <span className="font-medium truncate">{item.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 ml-5.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {typeLabels[item.type] || item.type}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusColors[item.status]}`}>
                          {item.status}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Editor */}
          <div className="flex-1 min-w-0">
            {!showEditor ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select an item or create a new one</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Action bar */}
                <Card>
                  <CardContent className="py-4 px-5 space-y-3">
                    <div>
                      <h2 className="text-lg font-semibold truncate">{isCreating ? "New Content Item" : title}</h2>
                      {selectedItem && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground font-mono">/{selectedItem.slug}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusColors[selectedItem.status]}`}>
                            {selectedItem.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Updated {formatDate(selectedItem.updated_at)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {previewUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {isCreating ? "Create" : "Save"}
                      </Button>
                      {selectedItem && selectedItem.status !== "published" && selectedItem.status !== "archived" && (
                        <Button size="sm" onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
                          <Send className="h-4 w-4 mr-1" />
                          Publish
                        </Button>
                      )}
                      <div className="flex-1" />
                      {selectedItem && selectedItem.status !== "archived" && (
                        <Button variant="destructive" size="sm" onClick={() => archiveMutation.mutate()}>
                          <Archive className="h-4 w-4 mr-1" />
                          Archive
                        </Button>
                      )}
                      {selectedItem && selectedItem.status === "archived" && (
                        <Button variant="outline" size="sm" onClick={() => changeStatus.mutate("draft")}>
                          Restore to Draft
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Validation errors */}
                {validationErrors.length > 0 && (
                  <Card className="border-destructive">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                        <div className="space-y-1">
                          {validationErrors.map((err, i) => (
                            <p key={i} className="text-sm text-destructive">{err}</p>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Tabs */}
                <Tabs defaultValue="fields">
                  <TabsList>
                    <TabsTrigger value="fields">Fields</TabsTrigger>
                    <TabsTrigger value="images">
                      <ImageIcon className="h-4 w-4 mr-1" />
                      Images
                    </TabsTrigger>
                    <TabsTrigger value="content">Content JSON</TabsTrigger>
                    <TabsTrigger value="seo">SEO</TabsTrigger>
                    {selectedItem && (
                      <TabsTrigger value="versions">
                        <History className="h-4 w-4 mr-1" />
                        Versions ({versions?.length || 0})
                      </TabsTrigger>
                    )}
                  </TabsList>

                  {/* Fields */}
                  <TabsContent value="fields">
                    <Card>
                      <CardContent className="py-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Type</Label>
                            <Select value={type} onValueChange={setType} disabled={!isCreating}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="blog">Blog Post</SelectItem>
                                <SelectItem value="guide">Guide</SelectItem>
                                <SelectItem value="case_study">Case Study</SelectItem>
                                <SelectItem value="faq">FAQ</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Slug</Label>
                            <Input
                              value={slug}
                              onChange={(e) => setSlug(e.target.value)}
                              placeholder="url-friendly-slug"
                              className="text-sm"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={title}
                            onChange={(e) => handleTitleChange(e.target.value)}
                            placeholder="Article title"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Excerpt</Label>
                          <Textarea
                            value={excerpt}
                            onChange={(e) => setExcerpt(e.target.value)}
                            placeholder="Brief summary shown in listing pages"
                            rows={3}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Images */}
                  <TabsContent value="images">
                    <Card>
                      <CardContent className="py-4 space-y-6">
                        <div className="grid md:grid-cols-3 gap-6">
                          <ImageUploader
                            label="Thumbnail Image"
                            value={thumbnailImage}
                            onChange={setThumbnailImage}
                            storagePath={type === "case_study" ? "case-studies" : "blog"}
                            showErrors={validationErrors.length > 0}
                          />
                          <ImageUploader
                            label={type === "case_study" ? "Contextual Image" : "Hero Image"}
                            value={heroImage}
                            onChange={setHeroImage}
                            storagePath={type === "case_study" ? "case-studies" : "blog"}
                            showErrors={validationErrors.length > 0}
                          />
                          <ImageUploader
                            label="OG Image (Social)"
                            value={ogImage}
                            onChange={setOgImage}
                            storagePath="og"
                            showErrors={validationErrors.length > 0}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Images are stored in structured folders. Alt text is required for publishing. Recommended: 1200×630 for OG images, 640×360 for thumbnails.
                        </p>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Content JSON */}
                  <TabsContent value="content">
                    <Card>
                      <CardContent className="py-4 space-y-2">
                        <Label>Content (structured JSON)</Label>
                        <Textarea
                          value={contentJson}
                          onChange={(e) => setContentJson(e.target.value)}
                          rows={20}
                          className="font-mono text-xs"
                          placeholder='{"category": "Strategy", "blocks": [...]}'
                        />
                        {parseContent() === null && (
                          <p className="text-sm text-destructive">Invalid JSON</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* SEO */}
                  <TabsContent value="seo">
                    <Card>
                      <CardContent className="py-4 space-y-4">
                        <div className="space-y-2">
                          <Label>
                            SEO Title
                            <span className={`ml-2 text-xs ${seoData.title.length > 60 ? "text-destructive" : "text-muted-foreground"}`}>
                              ({seoData.title.length}/60)
                            </span>
                          </Label>
                          <Input
                            value={seoData.title}
                            onChange={(e) => setSeoData((s) => ({ ...s, title: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>
                            Meta Description
                            <span className={`ml-2 text-xs ${
                              seoData.description.length > 160 ? "text-destructive" :
                              seoData.description.length < 140 && seoData.description.length > 0 ? "text-yellow-500" : "text-muted-foreground"
                            }`}>
                              ({seoData.description.length}/160)
                            </span>
                          </Label>
                          <Textarea
                            value={seoData.description}
                            onChange={(e) => setSeoData((s) => ({ ...s, description: e.target.value }))}
                            rows={3}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Canonical URL</Label>
                          <Input
                            value={seoData.canonicalUrl}
                            onChange={(e) => setSeoData((s) => ({ ...s, canonicalUrl: e.target.value }))}
                          />
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <div>
                            <Label>Allow Search Indexing</Label>
                            <p className="text-xs text-muted-foreground">Allow search engines to index this page</p>
                          </div>
                          <Switch
                            checked={seoData.indexable}
                            onCheckedChange={(v) => setSeoData((s) => ({ ...s, indexable: v }))}
                          />
                        </div>
                        <div className="border-t pt-4 space-y-4">
                          <h3 className="font-medium">Open Graph</h3>
                          <div className="space-y-2">
                            <Label>OG Title</Label>
                            <Input
                              value={seoData.ogTitle}
                              onChange={(e) => setSeoData((s) => ({ ...s, ogTitle: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>OG Description</Label>
                            <Textarea
                              value={seoData.ogDescription}
                              onChange={(e) => setSeoData((s) => ({ ...s, ogDescription: e.target.value }))}
                              rows={2}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>OG Image URL</Label>
                            <Input
                              value={seoData.ogImage}
                              onChange={(e) => setSeoData((s) => ({ ...s, ogImage: e.target.value }))}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Versions */}
                  {selectedItem && (
                    <TabsContent value="versions">
                      <Card>
                        <CardContent className="py-4">
                          {!versions || versions.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">
                              No versions yet. Versions are created on publish.
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {versions.map((v) => (
                                <div key={v.id} className="flex items-center justify-between p-3 border rounded-lg">
                                  <div>
                                    <p className="text-sm font-medium">{formatDate(v.created_at)}</p>
                                    <p className="text-xs text-muted-foreground">Snapshot at publish</p>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => rollbackMutation.mutate(v.id)}
                                    disabled={rollbackMutation.isPending}
                                  >
                                    <RotateCcw className="h-4 w-4 mr-1" />
                                    Restore
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContentItemsManager;
